import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import multiMonthPlugin from '@fullcalendar/multimonth';

import {
  FiPlus,
  FiLayers,
  FiSearch,
  FiChevronLeft,
  FiChevronRight,
  FiCalendar,
  FiX,
} from 'react-icons/fi';

import '../styles/CalenderSection.css';
import AddEvent from './AddEvent';
import { graphqlRequest } from '../../../utils/graphqlRequest';

const EVENT_COLORS = {
  MEETING: '#4CAF50',
  NOTE: '#2196F3',
  TASK: '#FF9800',
  CALL: '#9C27B0',
  EMAIL: '#FFEB3B',
  PAYMENT: '#F44336',
  DEADLINE: '#0D47A1',
};

function compactEventTitle(title, type, timeLabel) {
  const raw = String(title || '').trim();
  const lower = raw.toLowerCase();

  if (type === 'MEETING' && timeLabel) {
    const client = raw.replace(/^Meeting\s*[—-]\s*/i, '').replace(/\s*\([^)]*\)\s*$/, '');
    return `${timeLabel} · ${client || 'Meeting'}`;
  }

  const shortMap = [
    [/booking created/i, 'New booking'],
    [/quotation sent/i, 'Quotation'],
    [/invoice sent/i, 'Invoice'],
    [/custom email|custom mail/i, 'Email sent'],
    [/note reminder/i, 'Note reminder'],
    [/payment/i, 'Payment'],
  ];
  for (const [pattern, label] of shortMap) {
    if (pattern.test(lower)) return label;
  }

  return raw.length > 22 ? `${raw.slice(0, 20)}…` : raw;
}

// GRAPHQL QUERY
const GET_CALENDAR_EVENTS = `
  query GetCalendarEvents($filters: CalendarQueryInput) {
    getCalendarEvents(filters: $filters) {
      id
      title
      type
      date
      description
      bookingId
      assignedTo
      clientName
    }
  }
`;

export default function CalenderSection() {
  const calendarRef = useRef(null);
  const location = useLocation();

  const [allEvents, setAllEvents] = useState([]);
  const [localSearch, setLocalSearch] = useState('');
  const [currentDate, setCurrentDate] = useState('');
  const [view, setView] = useState('dayGridMonth');
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [calendarType, setCalendarType] = useState('');
  const [notification, setNotification] = useState(null);

  const urlSearch = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    return (params.get('search') || '').trim();
  }, [location.search]);

  useEffect(() => {
    if (urlSearch) setLocalSearch(urlSearch);
  }, [urlSearch]);

  const searchTerm = (localSearch || urlSearch).trim().toLowerCase();

  const events = useMemo(() => {
    if (!searchTerm) return allEvents;
    return allEvents.filter((ev) => {
      const haystack = [
        ev.title,
        ev.extendedProps?.fullTitle,
        ev.extendedProps?.description,
        ev.extendedProps?.type,
        ev.extendedProps?.assignedTo,
        ev.extendedProps?.bookingId,
      ]
        .map((v) => String(v || '').toLowerCase())
        .join(' ');
      return haystack.includes(searchTerm);
    });
  }, [allEvents, searchTerm]);

  const fetchAllEvents = useCallback(async () => {
    try {
      const data = await graphqlRequest(GET_CALENDAR_EVENTS, { filters: {} });
      const backendEvents = data.getCalendarEvents.map((ev) => {
        const start = new Date(ev.date);
        const isMeeting = ev.type === 'MEETING';
        const end = isMeeting
          ? new Date(start.getTime() + 30 * 60 * 1000)
          : undefined;
        const timeStr = start.toLocaleTimeString('en-AU', {
          hour: '2-digit',
          minute: '2-digit',
        });
        const displayTitle = compactEventTitle(ev.title, ev.type, isMeeting ? timeStr : null);

        return {
          id: ev.id,
          title: displayTitle,
          start: ev.date,
          end: end?.toISOString(),
          allDay: !isMeeting,
          backgroundColor: EVENT_COLORS[ev.type] || '#607D8B',
          borderColor: 'transparent',
          textColor: ev.type === 'EMAIL' ? 'black' : 'white',
          extendedProps: {
            fullTitle: ev.title,
            description: ev.description,
            type: ev.type,
            assignedTo: ev.assignedTo,
            bookingId: ev.bookingId,
            clientName: ev.clientName,
            when: start.toLocaleString('en-AU', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            }),
            timeLabel: isMeeting ? timeStr : null,
          },
        };
      });
      setAllEvents(backendEvents);
    } catch (err) {
      console.error('Error fetching events:', err);
    }
  }, []);

  useEffect(() => {
    fetchAllEvents();
  }, [fetchAllEvents]);

  const handleToday = () => {
    const api = calendarRef.current.getApi();
    api.today();
    setCurrentDate(api.view.title);
  };

  const handlePrev = () => {
    const api = calendarRef.current.getApi();
    api.prev();
    setCurrentDate(api.view.title);
  };

  const handleNext = () => {
    const api = calendarRef.current.getApi();
    api.next();
    setCurrentDate(api.view.title);
  };

  const handleViewChange = (value) => {
    const api = calendarRef.current.getApi();
    api.changeView(value);
    setView(value);
    setCurrentDate(api.view.title);
  };

  const showEventNotification = (event) => {
    const plainDescription = String(event.extendedProps.description || '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    setNotification({
      title: event.extendedProps.fullTitle || event.title,
      type: event.extendedProps.type,
      when: event.extendedProps.when || '—',
      description: plainDescription,
      assignedTo: event.extendedProps.assignedTo,
      clientName: event.extendedProps.clientName,
      color: event.backgroundColor,
    });

    setTimeout(() => setNotification(null), 8000);
  };

  return (
    <div className="calendar-section">
      {/* Top Toolbar */}
      <div className="calendar-topbar">
        <div className="left-controls">
          <button className="btn-calender" onClick={handleToday}>
            <FiCalendar /> Today
          </button>
        </div>

        <div className="middle-controls">
          <div className="search-wrapper-calender">
            <FiSearch className="search-icon-calender" />
            <input
              type="text"
              placeholder="Search events..."
              className="search-input-calender"
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="right-controls">
          <select
            className="select-calender"
            value={view}
            onChange={(e) => handleViewChange(e.target.value)}
          >
            <option value="multiMonthYear">Year</option>
            <option value="dayGridMonth">Month</option>
            <option value="timeGridWeek">Week</option>
          </select>
        </div>
      </div>

      <div className="calendar-nav">
        <div className="nav-center">
          <button className="btn-calender" onClick={handlePrev}>
            <FiChevronLeft />
          </button>
          <span className="current-date">{currentDate}</span>
          <button className="btn-calender" onClick={handleNext}>
            <FiChevronRight />
          </button>
        </div>
      </div>

      <FullCalendar
        ref={calendarRef}
        plugins={[
          dayGridPlugin,
          timeGridPlugin,
          interactionPlugin,
          multiMonthPlugin,
        ]}
        initialView="dayGridMonth"
        headerToolbar={false}
        events={events}
        height="auto"
        dayMaxEvents={3}
        moreLinkClick="popover"
        eventDisplay="block"
        selectable={false}
        eventClick={(info) => {
          info.jsEvent.preventDefault();
          showEventNotification(info.event);
        }}
        datesSet={(arg) => setCurrentDate(arg.view.title)}
        eventDidMount={(info) => {
          info.el.style.cursor = 'pointer';
        }}
      />

      {/* Add Event Modal */}
      {showAddEvent && (
        <AddEvent
          onClose={() => setShowAddEvent(false)}
          onSave={(newEvent) => {
            setAllEvents((prev) => [...prev, newEvent]);
            setShowAddEvent(false);
          }}
        />
      )}

      {/* Event Notification */}
      {notification && (
        <div
          className="event-notification"
          style={{ borderLeft: `6px solid ${notification.color}` }}
        >
          <div className="notification-header">
            <span>{notification.title}</span>
            <button onClick={() => setNotification(null)}>
              <FiX />
            </button>
          </div>
          <div className="notification-body">
            <p>
              <strong>When:</strong> {notification.when}
            </p>
            <p>
              <strong>Type:</strong> {notification.type}
            </p>
            {notification.clientName && (
              <p>
                <strong>Client:</strong> {notification.clientName}
              </p>
            )}
            {notification.assignedTo && (
              <p>
                <strong>{notification.type === 'MEETING' ? 'Host' : 'By'}:</strong>{' '}
                {notification.assignedTo}
              </p>
            )}
            {notification.description && (
              <p style={{ whiteSpace: 'pre-wrap' }}>
                <strong>Details:</strong> {notification.description}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
